import React, { useState } from 'react';
import { ShieldCheck, Info, Eye, Zap, AlertTriangle } from 'lucide-react';
import { AdSlotPlacementKey, AppView } from '../../types';
import { monetizationEngine } from '../../services/monetizationEngine';

interface Props {
  placementKey: AdSlotPlacementKey;
  currentView?: AppView;
  className?: string;
}

export const AdSlot: React.FC<Props> = ({ placementKey, currentView, className = '' }) => {
  const [dismissed, setDismissed] = useState(false);
  const telemetry = monetizationEngine.getTelemetry();
  const slots = monetizationEngine.getAdInventory();
  const slot = slots.find((s) => s.placementKey === placementKey);
  const isAllowed = monetizationEngine.isSlotAllowed(placementKey, currentView);

  if (dismissed || !isAllowed || !slot) {
    return null;
  }

  // Exact Dimension Container Reservation (CLS Guardrail)
  const containerStyle = {
    minHeight: `${slot.reservedDimensions.minHeight}px`,
  };

  return (
    <div
      className={`relative my-6 mx-auto w-full max-w-4xl bg-slate-50 border border-slate-200/90 rounded-xl p-3 text-center transition-all shadow-sm ${className}`}
      style={containerStyle}
    >
      {/* Required Identification Label (#30) - Anti-Deception Guardrail (#31) */}
      <div className="flex items-center justify-between border-b border-slate-200/60 pb-2 mb-2 text-[10px] uppercase font-bold tracking-wider text-slate-500">
        <div className="flex items-center gap-1.5">
          <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-extrabold text-[9px]">
            ADVERTISEMENT
          </span>
          <span className="text-slate-400 font-medium">| {slot.label}</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-slate-400 font-mono text-[9px]">
            {slot.reservedDimensions.width}x{slot.reservedDimensions.height}px reserved
          </span>
          {slot.placementKey === 'anchor_mobile' && (
            <button
              onClick={() => setDismissed(true)}
              className="text-slate-400 hover:text-slate-600 font-bold px-1"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Ad Serving Area or Controlled Test Mode Placeholder */}
      <div className="flex flex-col items-center justify-center min-h-[120px] bg-slate-100/80 rounded-lg p-4 border border-dashed border-slate-300 space-y-2">
        {telemetry.activeMode === 'TEST_MODE' ? (
          <div className="space-y-1 text-slate-600">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-800">
              <Zap className="w-4 h-4 text-amber-500" />
              YamooPay Monetization Test Mode ({slot.provider})
            </div>
            <p className="text-[11px] text-slate-500 max-w-md">
              Placement Key: <code className="font-mono text-slate-700">{slot.placementKey}</code>. No real ad impressions are requested in test mode.
            </p>
          </div>
        ) : (
          <div className="space-y-2 text-slate-700">
            <div className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-800">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span>Programmatic Display Inventory ({slot.provider})</span>
            </div>

            <p className="text-xs text-slate-500 max-w-lg leading-relaxed">
              Targeted contextual monetization slot. YamooPay Commercial Integrity Firewall enforces 0% interaction with financial calculation formulas.
            </p>

            <div className="flex items-center justify-center gap-3 text-[10px] text-slate-400 pt-1">
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3 text-blue-500" /> Viewability: {slot.viewabilityRate}%
              </span>
              <span>•</span>
              <span className="font-mono">Est. RPM: ${slot.estimatedRpm.toFixed(2)}</span>
              <span>•</span>
              <span className="text-emerald-700 font-medium">CLS Impact: {slot.clsImpact}</span>
            </div>
          </div>
        )}
      </div>

      {/* Footer Disclosure */}
      <div className="mt-2 text-[10px] text-slate-400 flex items-center justify-between">
        <span className="flex items-center gap-1">
          <Info className="w-3 h-3 text-slate-400" /> Ads do not influence financial comparison math or organic rankings.
        </span>
        <a href="#privacy" className="underline hover:text-slate-600">
          Ad Preferences
        </a>
      </div>
    </div>
  );
};
